import React from "react";
import { StatusFilterChips } from "../WerkbonViewShared";
import {
  formatDatum,
  formatDatumTijd,
  getStatusDotClass,
  isTodayDate,
  matchesStatusFilter,
} from "../../../pages/monteur/workbonViewUtils";
import WerkbonExecutionTab from "./WerkbonExecutionTab";
import {
  getDemoLocationName,
  getDemoProjectName,
  getDemoWorkorderTitle,
  useMarketingDemoMode,
} from "../../../utils/marketingDemo";

function normalizeWorkorderTypeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

const COMPACT_PROJECT_WORKORDER_KEYS = new Set([
  "project",
  "offerte_project",
  "inspectie_controle",
  "opname_voorbereiding",
]);

const EXTENDED_EXECUTION_WORKORDER_KEYS = new Set([
  "service_storing",
  "regie",
  "wachttijd_geen_bon",
]);

function isCompactProjectExecution(werkbon) {
  const effectiveLayout = String(werkbon?.effectiveUiPolicy?.layout || "").trim().toLowerCase();
  if (effectiveLayout === "compact") return true;
  if (effectiveLayout === "uitgebreid") return false;

  const candidates = [
    werkbon?.type,
    werkbon?.workorderType,
    werkbon?.workorderTypeConfig?.key,
    werkbon?.workorderTypeConfig?.systemKey,
  ]
    .map(normalizeWorkorderTypeKey)
    .filter(Boolean);

  if (candidates.some((key) => EXTENDED_EXECUTION_WORKORDER_KEYS.has(key))) return false;
  if (candidates.some((key) => COMPACT_PROJECT_WORKORDER_KEYS.has(key))) return true;
  return String(werkbon?.type || "").trim().toUpperCase() === "PROJECT";
}

export default function WerkbonTabs({
  TABS,
  activeTab,
  setActiveTab,
  agendaDate,
  statusFilter,
  setStatusFilter,
  agendaLoading,
  agendaError,
  agendaItems,
  markedWerkbonId,
  openWerkbon,
  activeWerkbonId,
  showTaakForm,
  setShowTaakForm,
  setTaakError,
  handleAddTaak,
  actieTypeDraft,
  setActieTypeDraft,
  ACTION_TYPE_OPTIONS,
  taakDraft,
  setTaakDraft,
  taakSaving,
  taakError,
  werkbonLoading,
  werkbonError,
  currentTaken,
  handleVeiligheidMelding,
  meldingText,
  setMeldingText,
  meldingType,
  setMeldingType,
  meldingSeverity,
  setMeldingSeverity,
  meldingPhoto,
  setMeldingPhoto,
  meldingSaving,
  meldingError,
  meldingSuccess,
  veiligheidsMeldingen,
  executionProps,
}) {
  const marketingDemoMode = useMarketingDemoMode();
  const compactProjectExecution = isCompactProjectExecution(executionProps?.werkbon);

  if (compactProjectExecution) {
    return <WerkbonExecutionTab {...executionProps} />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              activeTab === tab.key
                ? "monteur-tab-active bg-slate-900 text-white"
                : "monteur-tab-inactive border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "agenda" && (
        <section>
          <h3 className="mb-3 text-lg font-semibold">Planning {formatDatum(agendaDate)}</h3>
          <div className="mb-3 rounded-lg border border-gray-200 bg-white p-2">
            <p className="mb-1 text-xs text-gray-500">Ondersteunende filter</p>
            <StatusFilterChips value={statusFilter} onChange={setStatusFilter} />
          </div>
          {agendaLoading && <p className="text-gray-500">Agenda laden...</p>}
          {agendaError && <p className="text-red-600">{agendaError}</p>}
          {!agendaLoading &&
            !agendaError &&
            agendaItems.filter((item) => matchesStatusFilter(item?.status, statusFilter)).length === 0 && (
              <p className="text-gray-500">Geen geplande werkbonnen voor deze dag.</p>
            )}
          {!agendaLoading &&
            agendaItems.filter((item) => matchesStatusFilter(item?.status, statusFilter)).length > 0 && (
              <div className="space-y-2">
                <div className="space-y-2 md:hidden">
                  {agendaItems
                    .filter((item) => matchesStatusFilter(item?.status, statusFilter))
                    .map((item) => {
                      const fallbackIndex = Number(String(item?._id || "").slice(-2), 16) || 0;
                      const itemTitle = marketingDemoMode
                        ? getDemoWorkorderTitle(fallbackIndex)
                        : item.title || "-";
                      const itemLocation = marketingDemoMode
                        ? getDemoLocationName()
                        : item.location || "-";
                      const itemProjectInfo = marketingDemoMode
                        ? getDemoProjectName()
                        : item.projectInfo || "";
                      const isMarkedToday =
                        String(item?._id || "") === String(markedWerkbonId || "") &&
                        isTodayDate(item?.plannedDate);
                      const statusClass = getStatusDotClass(item?.status);
                      return (
                        <div
                          key={item._id}
                          className={`w-full rounded-lg border p-3 text-left ${
                            isMarkedToday ? "border-green-600 bg-green-600 text-white" : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold">{itemTitle}</p>
                            {isMarkedToday && <span className="sr-only">Geselecteerd</span>}
                          </div>
                          <p className={`text-xs ${isMarkedToday ? "text-green-100" : "text-gray-500"}`}>
                            {formatDatum(item.plannedDate)} {item.plannedStartTime || "-"}
                          </p>
                          <p className={`mt-1 text-xs ${isMarkedToday ? "text-green-50" : "text-gray-600"}`}>
                            {itemLocation} {itemProjectInfo ? `| ${itemProjectInfo}` : ""}
                          </p>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span
                              className={`inline-flex items-center gap-2 rounded-full px-2 py-1 ${
                                isMarkedToday ? "bg-green-700 text-green-50" : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              <span className={`h-2 w-2 rounded-full ${statusClass}`} />
                              {item.status || "-"}
                            </span>
                            {!marketingDemoMode && item.mapUrl ? (
                              <a
                                href={item.mapUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                className={
                                  isMarkedToday ? "text-green-50 underline" : "text-blue-600 underline"
                                }
                              >
                                Kaart
                              </a>
                            ) : (
                              <span className={isMarkedToday ? "text-green-100" : "text-gray-400"}>
                                {marketingDemoMode ? "Kaart verborgen in demo-mode" : "Geen kaart"}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 pr-3">Datum</th>
                        <th className="py-2 pr-3">Start</th>
                        <th className="py-2 pr-3">Titel</th>
                        <th className="py-2 pr-3">Locatie</th>
                        <th className="py-2 pr-3">Project</th>
                        <th className="py-2 pr-3">Kaart</th>
                        <th className="py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agendaItems
                        .filter((item) => matchesStatusFilter(item?.status, statusFilter))
                        .map((item) => {
                          const fallbackIndex = Number(String(item?._id || "").slice(-2), 16) || 0;
                          const itemTitle = marketingDemoMode
                            ? getDemoWorkorderTitle(fallbackIndex)
                            : item.title || "-";
                          const itemLocation = marketingDemoMode
                            ? getDemoLocationName()
                            : item.location || "-";
                          const itemProjectInfo = marketingDemoMode
                            ? getDemoProjectName()
                            : item.projectInfo || "-";
                          const isMarkedToday =
                            String(item?._id || "") === String(markedWerkbonId || "") &&
                            isTodayDate(item?.plannedDate);
                          const statusClass = getStatusDotClass(item?.status);
                          return (
                            <tr
                              key={item._id}
                              className={`cursor-pointer border-b ${
                                isMarkedToday ? "bg-green-600 text-white" : "hover:bg-gray-50"
                              }`}
                              onClick={() => openWerkbon(item._id)}
                            >
                              <td className="py-2 pr-3">{formatDatum(item.plannedDate)}</td>
                              <td className="py-2 pr-3">{item.plannedStartTime || "-"}</td>
                              <td className="py-2 pr-3">{itemTitle}</td>
                              <td className="py-2 pr-3">{itemLocation}</td>
                              <td className="py-2 pr-3">{itemProjectInfo}</td>
                              <td className="py-2 pr-3" onClick={(event) => event.stopPropagation()}>
                                {!marketingDemoMode && item.mapUrl ? (
                                  <a
                                    href={item.mapUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={
                                      isMarkedToday
                                        ? "text-green-50 hover:underline"
                                        : "text-blue-600 hover:underline"
                                    }
                                  >
                                    Open
                                  </a>
                                ) : (
                                  marketingDemoMode ? "Verborgen" : "-"
                                )}
                              </td>
                              <td className="py-2">
                                <span className="inline-flex items-center gap-2">
                                  <span className={`h-2 w-2 rounded-full ${statusClass}`} />
                                  {item.status || "-"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </section>
      )}

      {activeTab === "taken" && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">Acties op huidige werkbon</h3>
            {activeWerkbonId && (
              <button
                type="button"
                onClick={() => {
                  setShowTaakForm((prev) => !prev);
                  setTaakError("");
                }}
                className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                {showTaakForm ? "Sluiten" : "+ Actie"}
              </button>
            )}
          </div>
          <p className="mb-3 text-sm text-gray-600">
            Leg feitelijk vast wat nodig is om verder te kunnen, bijvoorbeeld ontbrekende informatie.
          </p>
          {!activeWerkbonId && (
            <p className="text-gray-500">Selecteer eerst een werkbon via de agenda of snelle keuze.</p>
          )}
          {activeWerkbonId && werkbonLoading && <p className="text-gray-500">Werkbon laden...</p>}
          {activeWerkbonId && werkbonError && <p className="text-red-600">{werkbonError}</p>}

          {activeWerkbonId && !werkbonLoading && showTaakForm && (
            <form onSubmit={handleAddTaak} className="mb-4 space-y-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Type actie</label>
                <select
                  value={actieTypeDraft}
                  onChange={(e) => setActieTypeDraft(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  {ACTION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={taakDraft}
                  onChange={(e) => setTaakDraft(e.target.value)}
                  placeholder="Wat heb je nodig om verder te kunnen?"
                  className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={taakSaving}
                  className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {taakSaving ? "Opslaan..." : "+ Actie"}
                </button>
              </div>
            </form>
          )}
          {taakError && <p className="mb-3 text-sm text-red-600">{taakError}</p>}

          {activeWerkbonId && !werkbonLoading && !werkbonError && currentTaken.length === 0 && (
            <p className="text-gray-500">
              Geen acties gevonden. Voeg een actie toe of gebruik de scope-items op deze werkbon.
            </p>
          )}
          {currentTaken.length > 0 && (
            <ul className="space-y-2">
              {currentTaken.map((taak, idx) => (
                <li key={`${taak.id || taak.type}-${idx}`} className="rounded border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    {taak.type}
                    {taak.status ? ` • ${taak.status}` : ""}
                  </p>
                  <p className="font-medium">{taak.text}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {activeTab === "veiligheid" && (
        <section>
          <h3 className="mb-3 text-lg font-semibold">Veiligheid melden</h3>
          {!activeWerkbonId && (
            <p className="mb-3 text-gray-500">Selecteer eerst een werkbon via de agenda of snelle keuze.</p>
          )}
          {activeWerkbonId && (
            <form onSubmit={handleVeiligheidMelding} className="space-y-3">
              <div className="grid gap-2 md:grid-cols-2">
                <label className="text-xs font-medium text-gray-600">
                  Type melding
                  <select
                    value={meldingType}
                    onChange={(e) => setMeldingType(e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="unsafe_situation">unsafe_situation</option>
                    <option value="near_miss">near_miss</option>
                    <option value="incident">incident</option>
                    <option value="stop_work">stop_work</option>
                    <option value="observation">observation</option>
                    <option value="other">other</option>
                  </select>
                </label>
                <label className="text-xs font-medium text-gray-600">
                  Ernst
                  <select
                    value={meldingSeverity}
                    onChange={(e) => setMeldingSeverity(e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                    <option value="critical">critical</option>
                  </select>
                </label>
              </div>
              <textarea
                value={meldingText}
                onChange={(e) => setMeldingText(e.target.value)}
                rows={4}
                placeholder="Omschrijf de veiligheidsmelding..."
                className="w-full rounded border border-gray-300 p-2 text-sm"
              />
              <label className="block text-xs font-medium text-gray-600">
                Foto toevoegen (optioneel)
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setMeldingPhoto(e.target.files?.[0] || null)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                />
                {meldingPhoto ? (
                  <span className="mt-1 block text-[11px] text-gray-500">{meldingPhoto.name}</span>
                ) : null}
              </label>
              <button
                type="submit"
                disabled={meldingSaving}
                className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
              >
                {meldingSaving ? "Opslaan..." : "Melding opslaan"}
              </button>
              {meldingError && <p className="text-sm text-red-600">{meldingError}</p>}
              {meldingSuccess && <p className="text-sm text-green-700">{meldingSuccess}</p>}
            </form>
          )}

          <div className="mt-6">
            <h4 className="mb-2 font-semibold">Recente veiligheidsmeldingen</h4>
            {veiligheidsMeldingen.length === 0 && (
              <p className="text-sm text-gray-500">Nog geen veiligheidsmeldingen.</p>
            )}
            {veiligheidsMeldingen.length > 0 && (
              <ul className="space-y-2">
                {veiligheidsMeldingen
                  .slice()
                  .map((melding, idx) => (
                    <li key={`${melding._id || melding.datum || "x"}-${idx}`} className="rounded border p-3">
                      <p className="text-xs text-gray-500">
                        {formatDatumTijd(melding.createdAt || melding.datum)} •{" "}
                        {String(melding.severity || "").trim() || "-"} •{" "}
                        {String(melding.status || "").trim() || "-"}
                      </p>
                      <p className="mt-1 text-sm font-medium">{melding.title || "-"}</p>
                      <p className="text-sm">{melding.description || melding.omschrijving || "-"}</p>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {activeTab === "werkbon" && <WerkbonExecutionTab {...executionProps} />}
    </>
  );
}
